import { defineStore } from 'pinia';
import { cloneDeep } from 'lodash';
import { useInventoryStore } from '@store';
import { useOrderStore } from '@store';

export const useMoveStore = defineStore('move', () => {
    const loading = ref(false);
    const data = ref({});
    const jobs = ref([]);
    const jobsStatuses = ref([]);

    const orderStore = useOrderStore();
    const inventoryStore = useInventoryStore();

    /*
     * Retrieve a move
     */
    async function retrieve(orderId = orderStore.getOrderId) {
        try {
            loading.value = true;
            const { data: resp } = await window.tenantAxios.get(`/orders/${orderId}/move`);
            data.value = resp.move;
            loading.value = false;
            return resp;
        } catch (error) {
            console.log('Error retrieving move', error);
            throw error;
        }
    }

    /*
     * Create a move
     */
    async function create(orderId = orderStore.getOrderId, moveData) {
        try {
            loading.value = true;
            const { data: resp } = await window.tenantAxios.post(`/orders/${orderId}/move`, {
                ...moveData,
            });
            data.value = resp.move;
            return resp;
        } catch (error) {
            console.log('Error creating move', error);
            throw error;
        } finally {
            loading.value = false;
        }
    }

    /*
     * Update a move
     */
    async function update(orderId = orderStore.getOrderId, moveData) {
        try {
            loading.value = true;
            const { data: resp } = await window.tenantAxios.patch(`/orders/${orderId}/move`, {
                ...moveData,
            });
            let temp = cloneDeep(data.value.inventory);
            data.value = resp.move;
            data.value.inventory = temp;
            return resp;
        } catch (error) {
            console.log('Error updating move', error);
            throw error;
        } finally {
            loading.value = false;
        }
    }

    async function inlineUpdate(orderId = orderStore.getOrderId, moveData) {
        try {
            const { data: resp } = await window.tenantAxios.patch(`/orders/${orderId}/move`, {
                ...moveData,
            });
            console.log('data', data);
            data.value = resp.move;
            return resp;
        } catch (error) {
            console.log('Error updating move', error);
            throw error;
        }
    }

    async function retrieveJobs(moveId = getMoveId.value) {
        try {
            const resp = await window.tenantAxios.get(`/moves/${moveId}/jobs?sort=-created_at`);
            jobs.value = resp.data.data;
            if (jobs.value.length > 0) {
                await retrieveJobsStatuses();
            }
            console.log(resp);
        } catch (e) {
            console.log('Store@move retrieveJobs() error', e);
            throw e;
        }
    }

    function resetJobs() {
        jobs.value = [];
        jobsStatuses.value = [];
    }

    async function retrieveJob(job_id) {
        try {
            const resp = await window.tenantAxios.get(`/moves/${getMoveId.value}/jobs/${job_id}`);
            return resp.data.job;
        } catch (e) {
            console.log('Store@move retrieveJobs() error', e);
            throw e;
        }
    }

    async function retrieveJobsStatuses(moveId = getMoveId.value) {
        jobsStatuses.value = [];
        jobs.value.forEach(async (job) => {
            const response = await window.tenantAxios.get(`/moves/${moveId}/jobs/${job.id}/statuses`);

            let formatted = [];

            for (const [key, val] of Object.entries(response.data.data)) {
                console.log(key, val);
                formatted.push({
                    name: val,
                    value: key,
                });
            }
            jobsStatuses.value.push({
                job_id: job.id,
                statuses: [...formatted],
            });
        });
        return jobsStatuses.value;
    }

    async function retrieveInventory() {
        try {
            await inventoryStore.retrieve(getMoveId.value);
        } catch (e) {
            console.log('Store@move retrieveInventory() error', e);
            throw e;
        }
    }

    // const getMoveRoutes = computedAsync(async () => {
    //     if (!orderStore.getOrderId) return;
    //
    //     try {
    //         const resp = await window.tenantAxios.get(`/orders/${orderStore.getOrderId}/move/routes`);
    //         return resp.data.map((route) => {
    //             return {
    //                 name: route.name,
    //                 id: route.id,
    //             };
    //         });
    //     } catch (e) {
    //         console.log('Store@move getMoveRoutes() error', e);
    //         throw e;
    //     }
    // });

    function setInventory(inventory) {
        data.value.inventory = inventory;
    }

    function setQuoteType(quoteType) {
        data.value.quote_type = quoteType;
    }

    function updateBundledAddon(value) {
        let index = data.value.bundledAddons.findIndex((el) => el.id == value.id);
        data.value.bundledAddons[index] = value;
    }

    // Match related

    async function selectMatch(matchId, orderId) {
        try {
            const response = await window.tenantAxios.patch(`/orders/${orderId}/move`, {
                service_match_id: matchId,
            });
            data.value = response.data.move;
            return response.data.move;
        } catch (error) {
            console.log('Error selecting match', error);
            throw error;
        }
    }

    async function applyCoupon(coupon) {
        try {
            await window.tenantAxios.post(`/coupons/${orderStore.getOrderId}/apply`, {
                coupon,
            });
            await retrieve();
        } catch (error) {
            console.log('Error revoking coupon', error);
            throw error;
        }
    }

    async function revokeCoupon() {
        try {
            await window.tenantAxios.delete(`/coupons/${orderStore.getOrderId}/remove`);
            await retrieve();
            await orderStore.retrieve();
            await orderStore.fetchItems();
        } catch (error) {
            console.log('Error revoking coupon', error);
            throw error;
        }
    }

    function setMoveId(id) {
        data.value.id = id;
    }

    function getJobsStatuses(jobId) {
        return jobsStatuses.value.find((el) => el.job_id == jobId)?.statuses;
    }

    function reset() {
        loading.value = false;
        data.value = {};
        jobs.value = [];
        jobsStatuses.value = [];
    }

    const getMoveId = computed(() => {
        return data.value?.id;
    });

    const getOrderItemId = computed(() => {
        return data.value?.order_item_id;
    });

    const getMove = computed(() => {
        return data.value;
    });

    const getQuoteType = computed(() => {
        return data.value?.quote_type;
    });

    const isAccurateQuote = computed(() => {
        return data.value?.quote_type === 'ACCURATE_QUOTE';
    });

    const isLocal = computed(() => {
        return data.value?.type === 'LOCAL';
    });

    const isInterstate = computed(() => {
        return data.value?.type === 'INTERSTATE';
    });

    const getType = computed(() => {
        return data.value?.type;
    });

    const isInventoryHeavy = computed(() => {
        const inventory = useInventoryStore();
        return inventory.hasHeavyItem;
    });

    const getSpace = computed(() => {
        return data.value.space;
    });

    const getSelectedMatch = computed(() => {
        return data.value?.selectedMatch;
    });

    const getCurrentJob = computed(() => {
        const matchableType = data.value?.selectedMatch?.matchable_type;
        const matchableId = data.value?.selectedMatch?.matchable_id;

        return (
            jobs.value?.find(
                (job) => job.current && job.matchable_type === matchableType && job.matchable_id === matchableId,
            ) ?? null
        );
    });

    const getJob = computed(() => {
        return data.value?.job;
    });

    const getStatus = computed(() => {
        return data.value?.status;
    });

    const getTimeEstimate = computed(() => {
        if (data.value?.time_est?.min && data.value?.time_est?.max) {
            return data.value.time_est.min + ' - ' + data.value.time_est.max + ' hrs';
        } else {
            return data.value.time_est?.time + ' hrs';
        }
    });

    const isMuvexpress = computed(() => {
        return getSelectedMatch.value?.match_type === 'MUVEXPRESS';
    });

    const isSelfPack = computed(() => {
        return getSelectedMatch.value?.match_type === 'SELF_PACK';
    });

    const isSelfy = computed(() => {
        return data.value?.is_selfy;
    });

    const isTaxibox = computed(() => {
        return data.value?.is_taxibox;
    });

    const hasBundledAddons = computed(() => {
        return data.value?.selectedMatch?.bundles?.length > 0;
    });

    const getBundledAddons = computed(() => {
        return data.value?.selectedMatch?.bundles;
    });

    const hasJobs = computed(() => {
        return jobs.value.length > 0;
    });

    const hasActionableJobs = computed(() => {
        return jobs.value.filter((job) => !['CANCELLED', 'DECLINED', 'WITHDRAWN'].includes(job.status)).length > 0;
    });

    const hasPendingJob = computed(() => {
        return jobs.value.find((job) => job.status === 'PENDING');
    });

    const hasAcceptedJob = computed(() => {
        return jobs.value.find((job) => job.status === 'ACCEPTED');
    });

    const hasReadyJob = computed(() => {
        return jobs.value.some((job) => job.status === 'READY');
    });

    const hasCompletedJob = computed(() => {
        return jobs.value.some((job) => job.status === 'COMPLETED');
    });

    const hasPendingInventory = computed(() => {
        return !!data.value?.pending_inventory?.length;
    });

    const getDatesFormatted = computed(() => {
        return {
            pickup_date_type: data.value.pickup_date_type,
            ...(data.value.pickup_date_from && {
                pickup_date_from: utcToUserTimezone(data.value.pickup_date_from),
            }),
            ...(data.value.pickup_date_by && {
                pickup_date_by: utcToUserTimezone(data.value.pickup_date_by),
            }),
            ...(data.value.pickup_date_interval && {
                pickup_date_interval: data.value.pickup_date_interval,
            }),
            ...(data.value.pickup_date_flexibility && {
                pickup_date_flexibility: data.value.pickup_date_flexibility,
            }),
        };
    });

    const getPickupAccessFormatted = computed(() => {
        if (data.value?.pickup_access?.length > 0) {
            return data.value.pickup_access.map((item) => {
                return {
                    name: item.accessTag.item,
                    unit: item.accessTag.unit,
                    access_tag_id: item.accessTag.id,
                    ...item?.accessTag,
                    value: item.value ?? null,
                };
            });
        } else {
            return [];
        }
    });

    const getDeliveryAccessFormatted = computed(() => {
        if (data.value?.delivery_access?.length > 0) {
            return data.value.delivery_access.map((item) => {
                return {
                    name: item.accessTag.item,
                    unit: item.accessTag.unit,
                    access_tag_id: item.accessTag.id,
                    ...item?.accessTag,
                    value: item.value ?? null,
                };
            });
        } else {
            return [];
        }
    });

    const getHideOrganiceMatches = computed(() => {
        return data.value?.hide_organic_matches ?? 0;
    });

    const getActiveJobsCount = computed(() => {
        return jobs.value.filter(
            (job) => job.status === 'ACCEPTED' || job.status === 'READY' || job.status === 'COMPLETED',
        ).length;
    });

    const getPickupAddressMapLink = computed(() => {
        if (getMove.value) {
            const link = new URL('https://maps.google.com');
            link.searchParams.append('q', getMove.value.pickup_address_formatted);

            return link.href;
        }

        return null;
    });

    const getDeliveryAddressMapLink = computed(() => {
        if (getMove.value) {
            const link = new URL('https://maps.google.com');
            link.searchParams.append('q', getMove.value.delivery_address_formatted);

            return link.href;
        }

        return null;
    });

    const getMoveInventoryCustomerUrl = computed(() => {
        if (!getMoveId.value) {
            return undefined;
        }
        return `/moves/${getMoveId.value}/inventory/customer`;
    });

    function getHasHeavyItems(inventoryOptions, getInventoryOption) {
        const inventory = data.value?.inventory;
        if (!inventory?.items || !inventoryOptions) {
            return false;
        }

        return inventory.items.some((room) =>
            room.items.some((item) => {
                const option = getInventoryOption(item.inventory_option_id);
                return option?.is_heavy && item.qty > 0;
            }),
        );
    }

    return {
        loading,
        data,
        jobs,
        jobsStatuses,
        retrieve,
        create,
        update,
        inlineUpdate,
        retrieveJobs,
        retrieveJob,
        resetJobs,
        retrieveJobsStatuses,
        retrieveInventory,
        setInventory,
        // getMoveRoutes,
        setQuoteType,
        updateBundledAddon,
        selectMatch,
        applyCoupon,
        revokeCoupon,
        getJobsStatuses,
        setMoveId,
        reset,
        getMoveId,
        getOrderItemId,
        getMove,
        getQuoteType,
        isAccurateQuote,
        isLocal,
        isInterstate,
        getType,
        isInventoryHeavy,
        getSpace,
        getSelectedMatch,
        getCurrentJob,
        getJob,
        getStatus,
        getTimeEstimate,
        isMuvexpress,
        isSelfPack,
        isSelfy,
        isTaxibox,
        hasBundledAddons,
        getBundledAddons,
        hasJobs,
        hasActionableJobs,
        hasPendingJob,
        hasAcceptedJob,
        hasReadyJob,
        hasCompletedJob,
        hasPendingInventory,
        getDatesFormatted,
        getPickupAccessFormatted,
        getDeliveryAccessFormatted,
        getHideOrganiceMatches,
        getActiveJobsCount,
        getPickupAddressMapLink,
        getDeliveryAddressMapLink,
        getMoveInventoryCustomerUrl,
        getHasHeavyItems,
    };
});
